/**
 * WeatherService.js
 * Fetches live weather data using Google Weather API (GET) with Open-Meteo fallback.
 */

const GOOGLE_API_KEY = "AIzaSyDLzC-FhQmB8JS2H46OW46DNOEboBbrZVg";

// ─── Centroid Computation ───────────────────────────────────────────────────

export const computeCentroidFromGeoJson = (geoJson) => {
  if (!geoJson) return null;
  const geometry = geoJson.geometry || geoJson;
  let totalLat = 0,
    totalLng = 0,
    count = 0;

  const processRing = (ring) => {
    ring.forEach(([lng, lat]) => {
      if (!isNaN(lat) && !isNaN(lng)) {
        totalLat += lat;
        totalLng += lng;
        count++;
      }
    });
  };

  if (geometry.type === "Polygon") {
    geometry.coordinates.forEach(processRing);
  } else if (geometry.type === "MultiPolygon") {
    geometry.coordinates.forEach((polygon) => polygon.forEach(processRing));
  }

  return count > 0 ? { lat: totalLat / count, lng: totalLng / count } : null;
};

// ─── Google Weather API (GET) ───────────────────────────────────────────────

export const fetchGoogleWeather = async (latitude, longitude) => {
  try {
    const url =
      `https://weather.googleapis.com/v1/currentConditions:lookup` +
      `?key=${GOOGLE_API_KEY}` +
      `&location.latitude=${latitude}` +
      `&location.longitude=${longitude}`;

    const response = await fetch(url, { method: "GET" });

    if (!response.ok) {
      console.warn(`Google Weather API returned ${response.status}, falling back to Open-Meteo`);
      return fetchOpenMeteoWeather(latitude, longitude);
    }

    const data = await response.json();
    return parseGoogleWeatherResponse(data, latitude, longitude);
  } catch (error) {
    console.warn("Google Weather API failed, falling back to Open-Meteo:", error);
    return fetchOpenMeteoWeather(latitude, longitude);
  }
};

const parseGoogleWeatherResponse = (data, lat, lng) => {
  if (!data) return null;

  // Extract condition from weatherCondition (actual API field name)
  const wc = data.weatherCondition || data.condition || {};
  const conditionText =
    wc.description?.text ||    // "Mostly sunny"
    wc.description ||          // fallback if description is a string
    wc.type ||                 // "MOSTLY_CLEAR"
    "Unknown";

  return {
    latitude: lat,
    longitude: lng,
    temperature: data.temperature?.degrees ?? null,
    temperatureUnit: data.temperature?.unit === "FAHRENHEIT" ? "°F" : "°C",
    feelsLike: data.feelsLikeTemperature?.degrees ?? null,
    humidity: data.relativeHumidity ?? null,
    windSpeed: data.wind?.speed?.value ?? null,
    windDirection: data.wind?.direction?.degrees ?? null,
    windDirectionCardinal: data.wind?.direction?.cardinal ?? "",
    uvIndex: data.uvIndex ?? null,
    visibility: data.visibility?.distance ?? null,
    pressure: data.airPressure?.meanSeaLevelMillibars ?? null,
    dewPoint: data.dewPoint?.degrees ?? null,
    cloudCover: data.cloudCover ?? null,
    condition: conditionText,
    conditionIcon: wc.iconBaseUri
      ? `${wc.iconBaseUri}/icon.png`
      : null,
    isDaytime: data.isDaytime ?? true,
    precipitation: data.precipitation?.qpf?.quantity ?? null,
    fetchedAt: new Date().toISOString(),
    source: "google",
  };
};

// ─── Open-Meteo Fallback (GET, Free, No Key) ───────────────────────────────

export const fetchOpenMeteoWeather = async (latitude, longitude) => {
  try {
    const params = [
      `latitude=${latitude}`,
      `longitude=${longitude}`,
      `current=temperature_2m,relative_humidity_2m,apparent_temperature,precipitation,weather_code,cloud_cover,wind_speed_10m,wind_direction_10m,surface_pressure,is_day`,
      `timezone=auto`,
    ].join("&");

    const url = `https://api.open-meteo.com/v1/forecast?${params}`;
    const response = await fetch(url, { method: "GET" });

    if (!response.ok) {
      console.error("Open-Meteo API error:", response.status);
      return null;
    }

    const data = await response.json();
    return parseOpenMeteoResponse(data, latitude, longitude);
  } catch (error) {
    console.error("Open-Meteo fetch error:", error);
    return null;
  }
};

const parseOpenMeteoResponse = (data, lat, lng) => {
  if (!data?.current) return null;
  const c = data.current;
  return {
    latitude: lat,
    longitude: lng,
    temperature: c.temperature_2m ?? null,
    temperatureUnit: "°C",
    feelsLike: c.apparent_temperature ?? null,
    humidity: c.relative_humidity_2m ?? null,
    windSpeed: c.wind_speed_10m ?? null,
    windDirection: c.wind_direction_10m ?? null,
    windDirectionCardinal: degreesToCardinal(c.wind_direction_10m),
    uvIndex: null,
    visibility: null,
    pressure: c.surface_pressure ?? null,
    dewPoint: null,
    cloudCover: c.cloud_cover ?? null,
    condition: weatherCodeToCondition(c.weather_code),
    conditionIcon: null,
    isDaytime: c.is_day === 1,
    precipitation: c.precipitation ?? null,
    fetchedAt: new Date().toISOString(),
    source: "open-meteo",
  };
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const degreesToCardinal = (deg) => {
  if (deg == null || isNaN(deg)) return "";
  const dirs = ["N","NNE","NE","ENE","E","ESE","SE","SSE","S","SSW","SW","WSW","W","WNW","NW","NNW"];
  return dirs[Math.round(deg / 22.5) % 16];
};

const weatherCodeToCondition = (code) => {
  if (code == null) return "Unknown";
  const map = {
    0: "Clear Sky", 1: "Mainly Clear", 2: "Partly Cloudy", 3: "Overcast",
    45: "Foggy", 48: "Depositing Rime Fog",
    51: "Light Drizzle", 53: "Moderate Drizzle", 55: "Dense Drizzle",
    56: "Freezing Light Drizzle", 57: "Freezing Dense Drizzle",
    61: "Slight Rain", 63: "Moderate Rain", 65: "Heavy Rain",
    66: "Freezing Light Rain", 67: "Freezing Heavy Rain",
    71: "Slight Snowfall", 73: "Moderate Snowfall", 75: "Heavy Snowfall", 77: "Snow Grains",
    80: "Slight Rain Showers", 81: "Moderate Rain Showers", 82: "Violent Rain Showers",
    85: "Slight Snow Showers", 86: "Heavy Snow Showers",
    95: "Thunderstorm", 96: "Thunderstorm w/ Slight Hail", 99: "Thunderstorm w/ Heavy Hail",
  };
  return map[code] || "Unknown";
};

export const getWeatherEmoji = (condition) => {
  if (!condition) return "🌡️";
  const lower = condition.toLowerCase();
  if (lower.includes("mainly clear")) return "🌤️";
  if (lower.includes("clear") || lower.includes("sunny")) return "☀️";
  if (lower.includes("partly cloudy")) return "⛅";
  if (lower.includes("cloud") || lower.includes("overcast")) return "☁️";
  if (lower.includes("fog")) return "🌫️";
  if (lower.includes("drizzle")) return "🌦️";
  if (lower.includes("rain") || lower.includes("shower")) return "🌧️";
  if (lower.includes("snow") || lower.includes("freezing")) return "❄️";
  if (lower.includes("thunder")) return "⛈️";
  if (lower.includes("hail")) return "🌨️";
  return "🌡️";
};

export const fetchWeatherForRegions = async (regions) => {
  if (!regions || regions.length === 0) return [];
  const results = [];
  const processedLocations = [];

  for (const region of regions) {
    const centroid = computeCentroidFromGeoJson(region.geoJson);
    if (!centroid) continue;

    const isDuplicate = processedLocations.some(
      (loc) => haversineDistance(loc.lat, loc.lng, centroid.lat, centroid.lng) < 10
    );
    if (isDuplicate) continue;
    processedLocations.push(centroid);

    const weather = await fetchGoogleWeather(centroid.lat, centroid.lng);
    if (weather) {
      results.push({
        ...weather,
        regionName: region.name || `Region ${region.gisId}`,
        gisId: region.gisId,
      });
    }
  }
  return results;
};

const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};